import './common.css'

import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { addServerErrors } from '@/components/forms/common/FormBase'
import { useModal } from '@/components/modal/Modal'
import { Spinner } from '@/components/spinner'
import { useAppContext } from '@/context/app'

export interface LocationRow {
  id: string
  name: string
  parentId?: string | null
  latitude?: number | null
  longitude?: number | null
  createdAt?: string
  updatedAt?: string
}

export interface LocationFormData {
  name: string
  parentId?: string | null
  latitude?: number | null
  longitude?: number | null
}

export const defaultLocationFormData = (parentId?: string | null): LocationFormData => ({
  name: '',
  parentId: parentId ?? null,
  latitude: null,
  longitude: null,
})

export const ROOT_LOCATION_ID = null

const getLocationChildren = (locations: LocationRow[], parentId: string | null) => {
  return locations
    .filter((location) => (location.parentId ?? null) === parentId)
    .sort((a, b) => a.name.localeCompare(b.name))
}

const getLocationPath = (locations: LocationRow[], selectedId: string | null) => {
  const path: LocationRow[] = []
  let currentId = selectedId

  while (currentId != null) {
    const row = locations.find((location) => location.id === currentId)
    if (!row) {
      break
    }
    path.unshift(row)
    currentId = row.parentId ?? null
  }

  return path
}

const collectDescendantIds = (locations: LocationRow[], rootId: string | undefined) => {
  if (rootId === undefined) {
    return new Set<string>()
  }

  const result = new Set<string>([rootId])
  const queue = [rootId]

  while (queue.length) {
    const currentId = queue.shift()
    if (currentId === undefined) {
      continue
    }

    const children = locations.filter((location) => (location.parentId ?? null) === currentId)
    for (const child of children) {
      if (!result.has(child.id)) {
        result.add(child.id)
        queue.push(child.id)
      }
    }
  }

  return result
}

export const LocationPathLabel = ({
  locations,
  selectedId,
  onClick,
}: {
  locations: LocationRow[]
  selectedId: string | null
  onClick: (row: LocationRow | null) => void
}) => {
  const { translates } = useAppContext()

  if (!translates) {
    return null
  }

  const path = getLocationPath(locations, selectedId)

  return <span className="remanent-locations-path">
    <span className="node" onClick={() => onClick(null)}>
      {translates.for("elements.path.root")}
    </span>

    {path.map((row) => (
      <React.Fragment key={row.id}>
        {" > "}
        <span className="node" onClick={() => onClick(row)}>
          {row.name}
        </span>
      </React.Fragment>
    ))}
  </span>
}

export const normalizeLocationPayload = (data: LocationFormData): LocationFormData => {
  const normalizedName = data.name.trim()
  const latitude = data.latitude === null || data.latitude === undefined || data.latitude === ('' as any)
    ? null
    : Number(data.latitude)
  const longitude = data.longitude === null || data.longitude === undefined || data.longitude === ('' as any)
    ? null
    : Number(data.longitude)

  return {
    name: normalizedName,
    parentId: data.parentId ?? null,
    latitude: Number.isNaN(latitude as number) ? null : latitude,
    longitude: Number.isNaN(longitude as number) ? null : longitude,
  }
}

export const LocationRemovePopup = ({
  onClose,
  location,
  onRemoved,
}: {
  onClose: () => void
  location?: LocationRow
  onRemoved: () => void
}) => {
  const { services, translates } = useAppContext()
  const [error, setError] = useState<string | null>(null)

  if (!location || !translates) {
    return null
  }

  const remove = async () => {
    setError(null)
    const response = await services.session.fetchWithToken(`/api/communal/remanent/locations/byid/${location.id}/delete`, {
      method: 'DELETE',
    })
    if (response.status !== 200) {
      const message = await response.text()
      setError(message || translates.for('elements.table.locations.remove_error'))
      return
    }
    onRemoved()
    onClose()
  }

  return <div className='dialog'>
    <div className='dialog-text'>
      {translates.for('elements.table.locations.remove_ask').replace('{name}', location.name)}
    </div>
    <div className='dialog-btns'>
      <div className='dialog-btn' onClick={() => onClose()}>
        <i className='svgfont-no' />
        {translates.for('elements.dialog.yesno.no')}
      </div>
      <div className='dialog-btn' onClick={remove}>
        <i className='svgfont-yes' />
        {translates.for('elements.dialog.yesno.yes')}
      </div>
      {error ? <div className='dialog-error'>{error}</div> : null}
    </div>
  </div>
}

export const ParentLocationSelectPopup = ({
  onClose,
  locations,
  selectedId,
  blockedIds,
  onApprove,
}: {
  onClose: () => void
  locations: LocationRow[]
  selectedId: string | null
  blockedIds?: Set<string>
  onApprove: (id: string | null) => void
}) => {
  const { translates } = useAppContext()
  const { openModal } = useModal()
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(selectedId ?? ROOT_LOCATION_ID)

  useEffect(() => {
    setCurrentNodeId(selectedId ?? ROOT_LOCATION_ID)
  }, [selectedId])

  const availableLocations = useMemo(() => {
    if (!blockedIds || blockedIds.size === 0) {
      return locations
    }
    return locations.filter((location) => !blockedIds.has(location.id))
  }, [locations, blockedIds])

  const currentPath = useMemo(() => {
    return getLocationPath(availableLocations, currentNodeId)
  }, [availableLocations, currentNodeId])

  const currentChildren = useMemo(() => {
    return getLocationChildren(availableLocations, currentNodeId)
  }, [availableLocations, currentNodeId])

  if (!translates) {
    return null
  }

  return <div className='main-form'>
    <div className='form-field'>
      <label>{translates.for('popup.parent_location_select.current_location')}</label>
      <div className='dialog-text'>
        <LocationPathLabel locations={availableLocations} selectedId={currentNodeId} onClick={(row) => {
          if (!row) {
            setCurrentNodeId(ROOT_LOCATION_ID)
            return
          }
          setCurrentNodeId(row.id)
        }} />
      </div>
    </div>

    <div className='form-field'>
      <label>{translates.for('popup.parent_location_select.children')}</label>
      <div className='remanent-locations-search-body'>
        {currentChildren.length === 0 ? <div className='empty-search-table-message'>{translates.for('popup.parent_location_select.empty')}</div> : null}
        {currentChildren.map((row) => (
          <div
            key={row.id}
            className='row'
            onClick={() => setCurrentNodeId(row.id)}
            style={{ cursor: 'pointer' }}
          >
            <div className='col'>{row.name}</div>
            <div className='col icocol'>
              <i className='svgfont-arrow-right' />
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className='form-buttons'>
      <button
        className='submit-button'
        type='button'
        onClick={() => {
          onApprove(currentNodeId)
          onClose()
        }}
      >
        {translates.for('popup.parent_location_select.accept')}
      </button>
    </div>
  </div>
}

export const LocationEditPopup = ({
  onClose,
  locationId,
  defaultParentId,
  onSaved,
}: {
  onClose: () => void
  locationId?: string
  defaultParentId?: string | null
  onSaved: () => void
}) => {
  const { services, session, translates } = useAppContext()
  const { openModal } = useModal()
  const form = useForm<LocationFormData>({
    mode: 'onChange',
    defaultValues: defaultLocationFormData(defaultParentId),
  })
  const formValues = form.watch()
  const [loading, setLoading] = useState<boolean>(!!locationId)
  const [locations, setLocations] = useState<LocationRow[]>([])
  const [submitErrors, setSubmitErrors] = useState<string[]>([])

  useEffect(() => {
    services.session.fetchWithToken('/api/communal/remanent/locations').then(async (response) => {
      if (response.status !== 200) {
        setLocations([])
        return
      }
      const data = await response.json()
      setLocations(Array.isArray(data) ? data : [])
    })
  }, [])

  useEffect(() => {
    if (locationId === undefined) {
      form.reset(defaultLocationFormData(defaultParentId))
      setLoading(false)
      return
    }

    setLoading(true)
    services.session.fetchWithToken(`/api/communal/remanent/locations/byid/${locationId}`)
      .then(async (response) => {
        if (response.status !== 200) {
          throw new Error(await response.text())
        }
        return response.json()
      })
      .then((data: LocationRow) => {
        form.reset({
          name: data.name || '',
          parentId: data.parentId ?? null,
          latitude: data.latitude ?? null,
          longitude: data.longitude ?? null,
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [locationId, defaultParentId])

  useEffect(() => {
    if (submitErrors.length === 0 && Object.keys(form.formState.errors).length === 0) {
      return
    }
    setSubmitErrors([])
    form.clearErrors()
  }, [formValues.name, formValues.parentId, formValues.latitude, formValues.longitude])

  const handle = async (data: LocationFormData) => {
    setSubmitErrors([])
    const payload = normalizeLocationPayload(data)
    const url = locationId === undefined
      ? '/api/communal/remanent/locations'
      : `/api/communal/remanent/locations/byid/${locationId}/update`
    const method = locationId === undefined ? 'POST' : 'PUT'

    const response = await services.session.fetchWithToken(url, {
      method,
      body: JSON.stringify(payload),
    })

    if (response.status !== 200) {
      const errors = await response.json().catch(() => null)
      if (errors) {
        addServerErrors(form, session.translates, errors)
        const translatedErrors = Object.values(errors as Record<string, { message?: string }>).map((error) => {
          if (!error?.message) {
            return translates.for('errors.general_error')
          }
          return translates.for(error.message)
        })
        setSubmitErrors(translatedErrors)
      } else {
        setSubmitErrors([translates.for('errors.general_error')])
      }
      return
    }

    onSaved()
    onClose()
  }

  if (!session || !translates || loading) {
    return <Spinner />
  }

  const currentId = locationId
  const blockedIds = collectDescendantIds(locations, currentId)
  const selectedParentId = form.watch('parentId') ?? null
  // const parentLocationLabel = getLocationPathLabel(locations.filter((location) => !blockedIds.has(location.id)), selectedParentId)

  return <form className='main-form' onSubmit={form.handleSubmit(handle)}>
    <div className='form-field'>
      <label>{translates.for('elements.table.locations.name')}</label>
      <input type='text' {...form.register('name', { required: true })} />
    </div>

    {locationId ? <div className='form-field'>
      <label>{translates.for('elements.table.locations.parent')}</label>
      <LocationPathLabel locations={locations} selectedId={selectedParentId} onClick={(row) => {
        if (!row) {
          form.setValue('parentId', ROOT_LOCATION_ID, {
            shouldDirty: true,
            shouldValidate: true,
          })
          return
        }
        if (selectedParentId === row.id) {
          return openModal((props) => <ParentLocationSelectPopup
            {...props}
            locations={locations}
            selectedId={row?.id}
            onApprove={(value) => {
              form.setValue('parentId', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }}
          />)
        }
        form.setValue('parentId', row.id, {
          shouldDirty: true,
          shouldValidate: true,
        })
      }} />
    </div> : null}

    {/* <div className='form-field'>
      <label>Latitude</label>
      <input
        type='number'
        step='0.000001'
        value={form.watch('latitude') ?? ''}
        onChange={(e) => form.setValue('latitude', e.target.value === '' ? null : Number(e.target.value), {
          shouldDirty: true,
          shouldValidate: true,
        })}
      />
    </div>

    <div className='form-field'>
      <label>Longitude</label>
      <input
        type='number'
        step='0.000001'
        value={form.watch('longitude') ?? ''}
        onChange={(e) => form.setValue('longitude', e.target.value === '' ? null : Number(e.target.value), {
          shouldDirty: true,
          shouldValidate: true,
        })}
      />
    </div> */}

    <div className='form-buttons'>
      <button className='submit-button' type='submit'>
        {locationId === undefined
          ? translates.for('elements.table.locations.add')
          : translates.for('elements.table.locations.save')}
      </button>
    </div>
    {submitErrors.length > 0 ? <div className='critic-error-box'>
      {submitErrors.map((error, index) => (
        <div key={error + index}>{error}</div>
      ))}
    </div> : null}
  </form>
}
